import { useEffect, useMemo, useState } from "react";
import { Package, Search, Download, Tag, ShieldCheck, Loader2, ArrowUpRight, Wrench } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { IS_INGAME } from "@/components/cef/cefShared";

const CATS = ["All", "Models", "Maps", "Scripts", "Vehicles", "Audio", "Gameplay", "Other"];

/* Mod Workshop — in-menu build. Same live catalog as /new-citizen/workshop,
   styled to the menu's tactical night-city vibe. Downloads open the author's
   link in a browser; inside the game client the bridge hands them to the mod installer. */
export function WorkshopPanel({ onBack, audio, accent = "#2E6BFF" }) {
  const [mods, setMods] = useState(null);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    setMods(null);
    api.get("/workshop/mods", { params: { category: cat === "All" ? "" : cat, q, sort } })
      .then((r) => setMods(r.data))
      .catch(() => setMods([]));
  }, [cat, q, sort]);

  const selected = useMemo(() => (mods || []).find((m) => m.id === sel) || null, [mods, sel]);

  const download = async (mod) => {
    audio.click();
    if (IS_INGAME) { toast.info("Mod downloads run on the desktop web workshop — the bridge installs from there"); return; }
    try {
      const r = await api.post(`/workshop/mods/${mod.id}/download`);
      window.open(r.data.file_url, "_blank", "noopener");
      setMods((ms) => ms.map((m) => (m.id === mod.id ? { ...m, downloads: (m.downloads || 0) + 1 } : m)));
    } catch {
      toast.error("Download link unavailable");
    }
  };

  return (
    <div data-testid="workshop-panel" className="absolute inset-0 flex flex-col" style={{ animation: "ncFadeIn 0.3s ease both" }}>
      <div className="flex items-center gap-3 px-6 sm:px-10 pt-20 flex-wrap">
        <button data-testid="workshop-back" onClick={onBack} className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-zinc-100 transition-colors mr-2">‹ Back</button>
        <h2 className="font-semibold uppercase tracking-[0.2em] text-lg sm:text-xl text-zinc-50 flex items-center gap-2.5"><Wrench className="w-5 h-5" style={{ color: accent }} /> Mod Workshop</h2>
        <span className="font-mono text-[10px] text-zinc-400 tracking-[0.2em]">{mods ? `${mods.length} mods` : "loading…"}</span>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="workshop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="SEARCH MODS"
              className="bg-black/50 border border-white/15 focus:border-[#2E6BFF]/60 outline-none pl-9 pr-3 py-2 font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-200 w-48 placeholder:text-zinc-500" />
          </div>
          <select data-testid="workshop-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="bg-black/50 border border-white/15 outline-none px-3 py-2 font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-300">
            <option value="new" className="bg-black">Newest</option>
            <option value="downloads" className="bg-black">Most downloaded</option>
          </select>
          {!IS_INGAME && (
            <a data-testid="workshop-full" href="/new-citizen/workshop" target="_blank" rel="noopener noreferrer"
              className="border border-white/15 hover:border-[#2E6BFF]/60 px-3 py-2 font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-300 flex items-center gap-1.5 transition-colors">
              Full workshop <ArrowUpRight className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
      <div className="flex gap-1.5 px-6 sm:px-10 mt-3 flex-wrap">
        {CATS.map((c) => (
          <button key={c} data-testid={`workshop-cat-${c.toLowerCase()}`} onClick={() => { setCat(c); audio.tick(); }}
            className={`px-3 py-1.5 border font-mono text-[10px] tracking-[0.15em] uppercase transition-colors ${cat === c ? "text-black" : "border-white/15 text-zinc-400 hover:border-white/40"}`}
            style={cat === c ? { backgroundColor: accent, borderColor: accent } : {}}>
            {c}
          </button>
        ))}
      </div>

      {/* live notice */}
      <div data-testid="workshop-notice" className="mx-6 sm:mx-10 mt-3 border border-nc-green/40 bg-nc-green/[0.06] px-5 py-4 flex items-start gap-3">
        <Package className="w-4 h-4 text-nc-green shrink-0 mt-0.5" />
        <div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-nc-green">Config packs are live</p>
          <p className="font-mono text-[11px] text-zinc-300 leading-relaxed mt-1">
            Official packs install in one click — menu themes (American / Australian / European) land in Settings → Menu Theme; server packs apply laws, radio plans and department structures straight to a server you own.
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex gap-4 px-6 sm:px-10 py-4">
        {/* mod grid */}
        <div className="flex-1 min-w-0 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 content-start" style={{ scrollbarWidth: "thin", scrollbarColor: "#2E6BFF44 transparent" }}>
          {!mods && <div className="col-span-full flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin" style={{ color: accent }} /></div>}
          {mods && mods.length === 0 && (
            <p data-testid="workshop-empty" className="col-span-full py-16 text-center font-mono text-[11px] text-zinc-500 uppercase tracking-[0.25em]">No mods here yet — publish one from the website workshop</p>
          )}
          {(mods || []).map((m, i) => (
            <button key={m.id} data-testid={`workshop-mod-${i}`} onClick={() => { setSel(m.id); audio.tick(); }}
              className={`text-left border bg-[#080a0f]/85 backdrop-blur-md transition-all hover:border-white/30 hover:-translate-y-0.5 ${sel === m.id ? "" : "border-white/10"}`}
              style={{ borderColor: sel === m.id ? accent : undefined, animation: `ncFadeUp 0.35s ease ${Math.min(i * 0.04, 0.5)}s both` }}>
              {m.image_url ? (
                <img src={m.image_url} alt="" className="w-full h-28 object-cover border-b border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="h-28 border-b border-white/10 flex items-center justify-center bg-black/40"><Package className="w-6 h-6 text-zinc-600" /></div>
              )}
              <div className="p-3.5 flex flex-col gap-1.5">
                <p className="font-semibold text-sm text-zinc-100 leading-tight truncate">{m.title}</p>
                <p className="font-mono text-[9px] text-zinc-400 uppercase tracking-[0.15em] flex items-center gap-1.5">
                  {m.author_name} {m.author_verified && <ShieldCheck className="w-3 h-3 text-emerald-400" />} · v{m.version}
                </p>
                <div className="flex items-center gap-2 font-mono text-[9px] text-zinc-500">
                  <span className="border border-white/10 px-1.5 py-0.5 uppercase">{m.category}</span>
                  <span className="flex items-center gap-1"><Download className="w-3 h-3" />{m.downloads || 0}</span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* detail card */}
        {selected && (
          <aside data-testid="workshop-detail" className="hidden md:flex w-[380px] shrink-0 border border-white/10 bg-[#080a0f]/90 backdrop-blur-md flex-col" style={{ animation: "ncSlideR 0.16s ease-out both" }}>
            <div className="h-[3px] w-full" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
            {selected.image_url && <img src={selected.image_url} alt="" className="w-full h-44 object-cover border-b border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            <div className="p-6 flex-1 flex flex-col gap-4 overflow-y-auto">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: accent }}>{selected.category} · v{selected.version}</p>
                <h3 className="font-bold uppercase text-xl tracking-wide text-zinc-50 mt-1 leading-tight">{selected.title}</h3>
                <p className="font-mono text-[10px] text-zinc-400 mt-1.5 flex items-center gap-1.5">
                  by {selected.author_name} {selected.author_verified && <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />}
                </p>
              </div>
              <p className="font-mono text-[11px] leading-relaxed text-zinc-300">{selected.description || "No description provided."}</p>
              {(selected.tags || []).length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {selected.tags.map((t) => <span key={t} className="font-mono text-[9px] uppercase border border-white/15 text-zinc-400 px-2 py-1 flex items-center gap-1"><Tag className="w-2.5 h-2.5" />{t}</span>)}
                </div>
              )}
              <div className="mt-auto pt-3">
                <button data-testid="workshop-download" onClick={() => download(selected)}
                  className="w-full font-bold uppercase tracking-[0.25em] text-sm py-4 text-black transition-transform hover:scale-[1.02] flex items-center justify-center gap-2"
                  style={{ backgroundColor: accent, boxShadow: `0 0 34px ${accent}44` }}>
                  <Download className="w-4 h-4" /> Get mod — {selected.downloads || 0} downloads
                </button>
                <p className="font-mono text-[9px] text-zinc-500 text-center mt-2 tracking-[0.15em] uppercase">opens the author's link · in-game the bridge installs it</p>
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
